import { z } from 'zod';
import { cursorPageSchema, cursorQuerySchema, isoDateTimeSchema, queryBooleanSchema, uuidSchema, localeSchema, } from './common.js';
import { recipeDraftSchema } from './recipe-draft.js';
/**
 * Tasting notes ("Eaten at …"): places, visits and the dishes eaten there.
 * See docs/finished/tasting-notes-spec.md. Venue lookup happens in the client (OpenStreetMap);
 * everything the client sends about a place is untrusted and length-limited here.
 */
export const TASTING_MAX_NOTES_PER_VISIT = 30;
export const TASTING_MAX_PHOTOS_PER_NOTE = 8;
export const TASTING_MAP_MAX_POINTS = 2000;
export const placeProviderSchema = z.enum(['OSM', 'MANUAL']);
export const locationSourceSchema = z.enum(['DEVICE', 'EXIF', 'MANUAL', 'NONE']);
export const visitVisibilitySchema = z.enum(['PRIVATE', 'HOUSEHOLD']);
/** Decimal degrees as JSON numbers (the decimal-string rule applies to quantities only). */
export const latitudeSchema = z.number().min(-90).max(90);
export const longitudeSchema = z.number().min(-180).max(180);
export const geoPointSchema = z.object({ lat: latitudeSchema, lng: longitudeSchema });
const shortText = (max) => z.string().trim().min(1).max(max);
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
const cuisinesSchema = z.array(z.string().trim().min(1).max(60)).max(12);
const countryCodeSchema = z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, 'Expected an ISO 3166-1 alpha-2 country code')
    .transform((v) => v.toUpperCase());
const websiteSchema = z
    .string()
    .trim()
    .max(500)
    .url()
    .refine((v) => /^https?:\/\//i.test(v), 'Only http(s) URLs are allowed');
// ---------------------------------------------------------------------------
// User preferences
// ---------------------------------------------------------------------------
export const tastingPreferencesSchema = z.object({
    /** Ask the device for its position when capturing a tasting note. */
    useLocation: z.boolean().default(true),
    /** New visits are shared with the default household. */
    shareByDefault: z.boolean().default(false),
    /** The one household visits are shared with (fixed default, decision 2026-09-18). */
    defaultHouseholdId: uuidSchema.nullable().default(null),
});
// ---------------------------------------------------------------------------
// Places
// ---------------------------------------------------------------------------
export const placeSchema = z.object({
    id: uuidSchema,
    provider: placeProviderSchema,
    providerRef: z.string().nullable(),
    name: z.string(),
    category: z.string().nullable(),
    cuisines: z.array(z.string()),
    lat: z.number(),
    lng: z.number(),
    street: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    countryCode: z.string().nullable(),
    website: z.string().nullable(),
    visitCount: z.number().int().nonnegative(),
    /** False for the place snapshot of a visit another household member shared. */
    own: z.boolean(),
    version: z.number().int(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const placePageSchema = cursorPageSchema(placeSchema);
export const placeWriteSchema = z
    .object({
    provider: placeProviderSchema,
    /** OSM element reference such as "node/123456". Required for OSM, absent for MANUAL. */
    providerRef: z
        .string()
        .trim()
        .regex(/^(node|way|relation)\/\d{1,20}$/)
        .nullable()
        .optional(),
    name: shortText(200),
    category: optionalText(60),
    cuisines: cuisinesSchema.default([]),
    lat: latitudeSchema,
    lng: longitudeSchema,
    street: optionalText(200),
    postalCode: optionalText(20),
    city: optionalText(120),
    region: optionalText(120),
    countryCode: countryCodeSchema.nullable().optional(),
    website: websiteSchema.nullable().optional(),
})
    .superRefine((value, ctx) => {
    if (value.provider === 'OSM' && !value.providerRef)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['providerRef'],
            message: 'providerRef is required for OSM places',
        });
    if (value.provider === 'MANUAL' && value.providerRef)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['providerRef'],
            message: 'Manual places have no providerRef',
        });
});
export const placeUpdateSchema = z.object({
    version: z.number().int().nonnegative(),
    name: shortText(200).optional(),
    category: optionalText(60),
    cuisines: cuisinesSchema.optional(),
    lat: latitudeSchema.optional(),
    lng: longitudeSchema.optional(),
    street: optionalText(200),
    postalCode: optionalText(20),
    city: optionalText(120),
    region: optionalText(120),
    countryCode: countryCodeSchema.nullable().optional(),
    website: websiteSchema.nullable().optional(),
});
export const placeListQuerySchema = cursorQuerySchema.extend({
    q: z.string().trim().max(200).optional(),
});
/** Merges `sourcePlaceId` into the place in the URL: visits move over, the source is deleted. */
export const placeMergeRequestSchema = z.object({ sourcePlaceId: uuidSchema });
export const placeDeleteQuerySchema = z.object({
    /** Visits of the deleted place are reassigned to this place. */
    reassignTo: uuidSchema.optional(),
});
// ---------------------------------------------------------------------------
// Tasting notes (one dish)
// ---------------------------------------------------------------------------
export const tastingPhotoSchema = z.object({
    assetId: uuidSchema,
    altText: z.string().nullable(),
    url: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    status: z.string(),
});
export const tastingPhotoInputSchema = z.object({
    assetId: uuidSchema,
    altText: z.string().trim().max(300).nullable().optional(),
});
export const tastingNoteRecipeLinkSchema = z.object({
    recipeId: uuidSchema,
    title: z.string(),
    aiGenerationId: uuidSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const tastingNoteSchema = z.object({
    id: uuidSchema,
    visitId: uuidSchema,
    /** Null when the author's account no longer exists. */
    authorId: uuidSchema.nullable(),
    authorName: z.string().nullable(),
    position: z.number().int(),
    dishName: z.string().nullable(),
    notes: z.string(),
    flavorTags: z.array(z.string()),
    rating: z.number().int().min(1).max(5).nullable(),
    photos: z.array(tastingPhotoSchema),
    recipes: z.array(tastingNoteRecipeLinkSchema),
    canEdit: z.boolean(),
    version: z.number().int(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
const noteFields = {
    dishName: optionalText(200),
    notes: z.string().trim().max(4000).default(''),
    flavorTags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    photos: z.array(tastingPhotoInputSchema).max(TASTING_MAX_PHOTOS_PER_NOTE).default([]),
};
export const tastingNoteWriteSchema = z
    .object(noteFields)
    .refine((v) => v.photos.length > 0 || v.notes.length > 0 || !!v.dishName, {
    message: 'A tasting note needs a photo, a dish name or some text',
});
export const tastingNoteUpdateSchema = z.object({
    version: z.number().int().nonnegative(),
    dishName: optionalText(200),
    notes: z.string().trim().max(4000).optional(),
    flavorTags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    photos: z.array(tastingPhotoInputSchema).max(TASTING_MAX_PHOTOS_PER_NOTE).optional(),
    position: z.number().int().min(0).max(TASTING_MAX_NOTES_PER_VISIT).optional(),
});
/**
 * Moves a dish into another visit of the same owner, or – with `newVisit` – into a fresh
 * visit that inherits time and position of the source but no place (e.g. when an offline
 * capture put two restaurants into one visit).
 */
export const tastingNoteMoveRequestSchema = z
    .object({ targetVisitId: uuidSchema.optional(), newVisit: z.boolean().optional() })
    .refine((v) => Boolean(v.targetVisitId) !== Boolean(v.newVisit), {
    message: 'Provide either targetVisitId or newVisit',
    path: ['targetVisitId'],
});
/** New order of all dishes of a visit (every dish exactly once). Visit owner only. */
export const visitNoteOrderSchema = z.object({
    version: z.number().int().nonnegative(),
    noteIds: z.array(uuidSchema).min(1).max(TASTING_MAX_NOTES_PER_VISIT),
});
export const linkTastingRecipeRequestSchema = z.object({
    recipeId: uuidSchema,
    aiGenerationId: uuidSchema.nullable().optional(),
    /**
     * One of the dish's photos: a copy of it becomes the recipe's picture when the recipe has
     * none. A copy, so the recipe keeps its picture when the visit is deleted.
     */
    coverFromPhotoAssetId: uuidSchema.nullable().optional(),
});
// ---------------------------------------------------------------------------
// Visits
// ---------------------------------------------------------------------------
export const visitPermissionsSchema = z.object({
    /** Change place, time, comment, visibility; merge; delete. Visit owner only. */
    canManage: z.boolean(),
    /** Add dishes. Owner, or household OWNER/EDITOR on a shared visit. */
    canAddNotes: z.boolean(),
    canSuggestRecipes: z.boolean(),
});
export const visitSchema = z.object({
    id: uuidSchema,
    ownerId: uuidSchema,
    ownerName: z.string(),
    householdId: uuidSchema.nullable(),
    visibility: visitVisibilitySchema,
    place: placeSchema.nullable(),
    visitedAt: isoDateTimeSchema,
    comment: z.string().nullable(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
    accuracyM: z.number().int().nullable(),
    locationSource: locationSourceSchema,
    city: z.string().nullable(),
    region: z.string().nullable(),
    countryCode: z.string().nullable(),
    notes: z.array(tastingNoteSchema),
    permissions: visitPermissionsSchema,
    version: z.number().int(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const visitPageSchema = cursorPageSchema(visitSchema);
const visitLocationFields = {
    lat: latitudeSchema.nullable().optional(),
    lng: longitudeSchema.nullable().optional(),
    accuracyM: z.number().int().min(0).max(100000).nullable().optional(),
    locationSource: locationSourceSchema.optional(),
    city: optionalText(120),
    region: optionalText(120),
    countryCode: countryCodeSchema.nullable().optional(),
};
export const visitCreateSchema = z
    .object({
    placeId: uuidSchema.nullable().optional(),
    visitedAt: isoDateTimeSchema.optional(),
    comment: optionalText(2000),
    /** Omitted → the user's default (tastingPreferences.shareByDefault). */
    visibility: visitVisibilitySchema.optional(),
    ...visitLocationFields,
    notes: z.array(tastingNoteWriteSchema).min(1).max(TASTING_MAX_NOTES_PER_VISIT),
})
    .refine((v) => (v.lat == null) === (v.lng == null), {
    message: 'lat and lng must be provided together',
    path: ['lat'],
});
export const visitUpdateSchema = z
    .object({
    version: z.number().int().nonnegative(),
    placeId: uuidSchema.nullable().optional(),
    visitedAt: isoDateTimeSchema.optional(),
    comment: optionalText(2000),
    visibility: visitVisibilitySchema.optional(),
    ...visitLocationFields,
})
    .refine((v) => (v.lat === undefined) === (v.lng === undefined), {
    message: 'lat and lng must be provided together',
    path: ['lat'],
});
/** Moves every dish of `sourceVisitId` into the visit in the URL and deletes the source. */
export const visitMergeRequestSchema = z.object({ sourceVisitId: uuidSchema });
export const visitScopeSchema = z.enum(['all', 'mine', 'household']);
const visitFilterFields = {
    scope: visitScopeSchema.default('all'),
    placeId: uuidSchema.optional(),
    q: z.string().trim().max(200).optional(),
    countryCode: countryCodeSchema.optional(),
    city: z.string().trim().max(120).optional(),
    cuisine: z.string().trim().max(60).optional(),
    minRating: z.coerce.number().int().min(1).max(5).optional(),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
    hasRecipe: queryBooleanSchema.optional(),
};
export const visitListQuerySchema = cursorQuerySchema.extend(visitFilterFields);
/** "west,south,east,north" in decimal degrees. */
export const bboxSchema = z
    .string()
    .regex(/^-?\d+(\.\d+)?(,-?\d+(\.\d+)?){3}$/, 'Expected "west,south,east,north"')
    .transform((v) => {
    const [west, south, east, north] = v.split(',').map(Number);
    return { west, south, east, north };
})
    .refine((b) => b.west >= -180 &&
    b.west <= 180 &&
    b.east >= -180 &&
    b.east <= 180 &&
    b.south >= -90 &&
    b.north <= 90 &&
    b.south <= b.north, 'Bounding box is out of range');
export const visitMapQuerySchema = z.object({ bbox: bboxSchema.optional(), ...visitFilterFields });
export const visitMapPointSchema = z.object({
    visitId: uuidSchema,
    lat: z.number(),
    lng: z.number(),
    placeId: uuidSchema.nullable(),
    placeName: z.string().nullable(),
    visitedAt: isoDateTimeSchema,
    dishNames: z.array(z.string()),
    thumbnailUrl: z.string().nullable(),
    own: z.boolean(),
});
export const visitMapResponseSchema = z.object({
    points: z.array(visitMapPointSchema),
    truncated: z.boolean(),
});
// ---------------------------------------------------------------------------
// AI recipe suggestions
// ---------------------------------------------------------------------------
export const TASTING_SUGGESTION_SCHEMA_VERSION = '1.0.0';
export const ingredientEvidenceSchema = z.enum([
    'OBSERVED',
    'VISIBLE',
    'MENU',
    'SOURCE',
    'TYPICAL',
]);
export const tastingSuggestionRequestSchema = z.object({
    language: localeSchema.optional(),
    maxSuggestions: z.number().int().min(1).max(3).default(3),
});
export const tastingSuggestionSourceSchema = z.object({
    title: z.string(),
    url: z.string().url(),
    domain: z.string(),
    kind: z.enum(['RECIPE', 'MENU']),
});
export const tastingSuggestionSchema = z.object({
    title: z.string(),
    /** Why this dish fits, including how the described properties are explained. */
    rationale: z.string(),
    /** Always true: the result approximates the dish, it is not the restaurant's recipe. */
    approximation: z.literal(true),
    /** False when no retrieved source backs this suggestion. */
    sourced: z.boolean(),
    sources: z.array(tastingSuggestionSourceSchema),
    /** Evidence per ingredient, keyed by the ingredient name used in the draft. */
    evidence: z.array(z.object({ ingredient: z.string(), evidence: ingredientEvidenceSchema })),
    draft: recipeDraftSchema,
});
export const tastingSuggestionResultSchema = z.object({
    noteId: uuidSchema,
    dishCandidates: z.array(z.object({ name: z.string(), confidence: z.number().min(0).max(1) })),
    visibleComponents: z.array(z.string()),
    menuMatch: z
        .object({ name: z.string(), description: z.string().nullable(), url: z.string().url() })
        .nullable(),
    suggestions: z.array(tastingSuggestionSchema),
    /** True when source search was disabled/unavailable or found nothing reliable. */
    noReliableSource: z.boolean(),
    providerNote: z.string().nullable(),
});
/** Stored on AIGeneration.input. Never contains coordinates or the street address. */
export const aiTastingNoteInputSchema = z.object({
    noteId: uuidSchema,
    language: localeSchema.optional(),
    maxSuggestions: z.number().int().min(1).max(3).default(3),
});
/** "Inspired by" card on a recipe: the dish (and visit) it was created from. */
export const tastingRecipeOriginSchema = z.object({
    noteId: uuidSchema,
    visitId: uuidSchema,
    dishName: z.string().nullable(),
    placeName: z.string().nullable(),
    city: z.string().nullable(),
    visitedAt: isoDateTimeSchema,
});
//# sourceMappingURL=tasting.js.map